import * as React from 'react'

interface TextInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  error?: boolean
  helperText?: string
  endAdornment?: React.ReactNode
  /** render a <textarea> instead of an <input> */
  multiline?: boolean
  /** number of rows when multiline */
  rows?: number
  /** outer wrapper classes; forms that manage their own spacing pass '' */
  containerClassName?: string
}

export const TextInput: React.FC<TextInputProps> = ({
  label,
  value,
  onChange,
  error = false,
  helperText,
  endAdornment,
  disabled,
  type = 'text',
  multiline = false,
  rows = 4,
  containerClassName = 'mt-4',
  ...rest
}) => {
  const id = React.useId()

  const fieldClassName = `
    block w-full rounded-md border px-3 py-2.5 text-sm text-gray-900
    outline-none transition-colors
    placeholder:text-gray-400
    disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
    ${error
      ? 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500'
      : 'border-gray-300 focus:border-brand focus:ring-1 focus:ring-brand'
    }
    ${endAdornment ? 'pr-10' : ''}
  `

  return (
    <div className={containerClassName}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <div className="relative">
        {multiline ? (
          <textarea
            id={id}
            rows={rows}
            value={value}
            onChange={onChange}
            disabled={disabled}
            className={fieldClassName}
            {...(rest as unknown as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
          />
        ) : (
          <input
            id={id}
            type={type}
            value={value}
            onChange={onChange}
            disabled={disabled}
            className={fieldClassName}
            {...rest}
          />
        )}
        {endAdornment && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-2">
            {endAdornment}
          </div>
        )}
      </div>
      {helperText && (
        <p className={`mt-1 text-xs ${error ? 'text-red-500' : 'text-gray-500'}`}>
          {helperText}
        </p>
      )}
    </div>
  )
}
